import { useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { categories } from "@/mocks/home";

const categoryTabs = [
  { slug: "fashion", label: "时尚服饰" },
  { slug: "beauty", label: "美妆护肤" },
  { slug: "food", label: "美食特产" },
  { slug: "craft", label: "工艺精品" },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeTab = slug || "fashion";

  useEffect(() => {
    if (!categoryTabs.find((t) => t.slug === slug)) {
      navigate("/category/fashion", { replace: true });
    }
  }, [slug, navigate]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab),
    [activeTab]
  );

  const handleTabClick = (tabSlug: string) => {
    navigate(`/category/${tabSlug}`);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: currentCategory?.name || "品类" },
          ]}
        />

        {/* Category Hero */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-3">
                  <strong>{currentCategory?.name}</strong>
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-lg">
                  {currentCategory?.description}
                </p>
              </div>
              <div className="w-full lg:w-[360px] aspect-[16/10] rounded-lg overflow-hidden">
                <img
                  src={currentCategory?.image}
                  alt={currentCategory?.name}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tab Switcher */}
        <section className="w-full bg-background-50 border-b border-background-200/70 sticky top-16 md:top-18 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.slug}
                  onClick={() => handleTabClick(tab.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer border-none ${
                    activeTab === tab.slug
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {currentCategory?.items.map((item, i) => (
                <div
                  key={i}
                  className="group rounded-lg overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {item.origin}
                      </span>
                      <span className="text-xs text-foreground-500">{item.brand}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1">
                      {item.name}
                    </h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm md:text-base font-semibold text-accent-600">
                        {item.price}
                      </span>
                      <div className="flex items-center gap-0.5 text-accent-500">
                        <i className="ri-star-fill text-xs" />
                        <span className="text-xs font-medium">{item.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}